<template>
  <div class="cashcall-wrapper">
    <!-- Enable Cash Call (only for Underwriter and Broker) -->
    <template v-if="company.role && company.role.role === 'Underwriter'">
      <div class="d-flex align-items-center mb-4">
        <label class="mb-0 mr-5 font-weight-bold">
          Enable Cash Call?
        </label>
  
        <base-switch
          :value="form.cash_call_enabled"
          onText=""
          offText=""
          @input="(value) => (form.cash_call_enabled = value)"
        ></base-switch>
      </div>
  
      <!-- Assign TPAs -->
      <div v-if="form.cash_call_enabled" class="mb-4 tpa-dropdown-wrapper">
        <label class="font-weight-bold mb-2">
          Assign TPAs
        </label>
  
        <AdvancedDropdown
          v-model="form.cash_call_assigned_tpa_ids"
          :options="tpaOptions"
          multiple
          placeholder="Select TPAs"
        />
      </div>

      <div v-if="assignedTpas.length" class="assigned-tpa-list mt-3">
        <label class="font-weight-bold mb-1">Assigned TPAs</label>

        <div class="d-flex flex-wrap gap-2">
            <b-badge
            v-for="tpa in assignedTpas"
            :key="tpa.value"
            variant="info"
            class="px-2 py-1 ml-2"
            >
            {{ tpa.text }}
            </b-badge>
        </div>
      </div>
      <!-- Actions -->
      <div class="mt-4 d-flex gap-3">
        <BaseButton variant="secondary" @click="reset">
          Cancel
        </BaseButton>
        <BaseButton :disabled="!isDirty || saving" @click="save">
          <span v-if="saving">
            <b-spinner small class="mr-2" />
            Saving...
          </span>
          <span v-else>
            Save
          </span>
        </BaseButton>
      </div>
    </template>

    <!-- Cash Call Internal Approval (only for TPA) -->
    <template v-if="company.role && company.role.role === 'Broker'">
      <div class="approval-card mb-4">
        <div class="d-flex align-items-center justify-content-between pr-4">
          <div class="pr-2">
            <p class="font-weight-bold mb-1">View Cash Call</p>
          </div>
          <base-switch
            :value="form.cash_call_enabled"
            class="ml-4"
            onText=""
            offText=""
            @click.prevent="() => confirmBrokerToggle()"
          ></base-switch>
        </div>
      </div>
    </template>

    <!-- Cash Call Internal Approval (only for TPA) -->
    <template v-if="company.role && company.role.role === 'TPA'">
      <div class="approval-card mb-4">
        <div class="d-flex align-items-center justify-content-between pr-4">
          <div class="pr-2">
            <p class="font-weight-bold mb-1">Cash Call Internal Approval Required</p>
            <p class="text-muted mb-0 small">
              When enabled, cash calls must be approved by a designated approver
              before being sent to the market
            </p>
          </div>
          <base-switch
            :value="form.cash_call_internal_approval_required"
            class="ml-4"
            onText=""
            offText=""
            @click.prevent="() => confirmTpaToggle()"
          ></base-switch>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
import { CompanyService } from '@/services/CompanyService'
import { AdvancedDropdown } from '@components/advanced'
import { BaseButton } from '@components/base'

export default {
  name: 'ManageCompanyCashCall',
  components: {
    AdvancedDropdown,
    BaseButton
  },

  props: {
    company: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      form: {
        cash_call_enabled: false,
        cash_call_assigned_tpa_ids: [],
        cash_call_internal_approval_required: false
      },
      tpaOptions: [],
      original: null,
      saving: false,
    }
  },

  watch: {
    company: {
      immediate: true,
      handler(val) {
        if (!val || !val.role) return

        const roleValue = val.role.role

        if (roleValue === 'Underwriter') {
          this.form = {
            cash_call_enabled: !!val.cash_call_enabled,
            cash_call_assigned_tpa_ids: [...(val.cash_call_assigned_tpa_ids || [])],
          }
          this.loadTPAs()

        } else if (roleValue === 'TPA') {
          this.form = {
            cash_call_internal_approval_required: !!val.tpa_internal_approval_required,
          }
          this.original = JSON.stringify(this.form)
        } else if (roleValue === 'Broker') {
          this.form = {
            cash_call_enabled: !!val.cash_call_enabled,
          }
          this.original = JSON.stringify(this.form)
        }
      }
    }
  },

  computed: {
    assignedTpas() {
      return this.tpaOptions.filter(opt =>
        this.form.cash_call_assigned_tpa_ids.includes(opt.value)
      )
    },
    isDirty() {
      return JSON.stringify(this.form) !== this.original
    }
  },

  methods: {
    async loadTPAs() {
      const res = await CompanyService.lightweight({ role: 'TPA' })
      const rows = res && res.data && res.data.data || []
      this.tpaOptions = rows.map(tpa => ({
        text: tpa.name,
        value: tpa.id
      }))
      this.original = JSON.stringify(this.form)
    },

    reset() {
      this.form = JSON.parse(this.original)
    },

    async confirmTpaToggle() {
      const newValue = !this.form.cash_call_internal_approval_required

      await this.$swal({
        title: 'Are you sure?',
        text: newValue
          ? 'This will require internal approval before cash calls are sent to the market.'
          : 'This will remove the internal approval requirement.',
        showCancelButton: true,
        confirmButtonText: 'CONFIRM',
        cancelButtonText: 'CANCEL',
        showLoaderOnConfirm: true,
        allowOutsideClick: false,
        buttonsStyling: false,
        customClass: {
          popup: 'manage-cash-call-approval-btn-swal',
        },
        preConfirm: async (confirm) => {
          if (!confirm) return false
          try {
            this.form.cash_call_internal_approval_required = newValue
            await this.save()
          } catch (err) {
            const msg =
              (err && err.response && err.response.data && err.response.data.message) ||
              'Failed to save Cash Call settings'
            this.$swal.showValidationMessage(msg)
            throw err
          }
        }
      })
    },

    async confirmBrokerToggle() {
      const newValue = !this.form.cash_call_enabled

      await this.$swal({
        title: 'Are you sure?',
        text: newValue
          ? 'This will allow them to view and submit Cash Call requests.'
          : 'They will no longer be able to view or submit Cash Call requests.',
        showCancelButton: true,
        confirmButtonText: 'CONFIRM',
        cancelButtonText: 'CANCEL',
        showLoaderOnConfirm: true,
        allowOutsideClick: false,
        buttonsStyling: false,
        customClass: {
          popup: 'manage-cash-call-approval-btn-swal',
        },
        preConfirm: async (confirm) => {
          if (!confirm) return false
          try {
            this.form.cash_call_enabled = newValue
            await this.save()
          } catch (err) {
            const msg =
              (err && err.response && err.response.data && err.response.data.message) ||
              'Failed to save Cash Call settings'
            this.$swal.showValidationMessage(msg)
            throw err
          }
        }
      })
    },

    async save() {
      try {
        this.saving = true

        let payload

        if (this.company.role.role === 'TPA') {
          payload = {
            tpa_internal_approval_required: this.form.cash_call_internal_approval_required
          }
        } else if (this.company.role.role === 'Broker') {
          payload = {
            cash_call_enabled_input: this.form.cash_call_enabled
          }
        } else {
          // Underwriter
          payload = {
            cash_call_enabled_input: this.form.cash_call_enabled,
            cash_call_assigned_tpa_ids_input: this.form.cash_call_assigned_tpa_ids
          }
        }

        await CompanyService.patch(this.company.id, payload)

        this.$bvToast.toast('Cash Call settings saved successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })

        this.original = JSON.stringify(this.form)

        if (this.company.role.role === 'Underwriter') {
          this.company.cash_call_enabled = this.form.cash_call_enabled
          this.company.cash_call_assigned_tpa_ids = [
            ...this.form.cash_call_assigned_tpa_ids
          ]
        }
        else if (this.company.role.role === 'TPA') {
          this.company.tpa_internal_approval_required = this.form.cash_call_internal_approval_required
        }
        else if (this.company.role.role === 'Broker') {
          this.company.cash_call_enabled = this.form.cash_call_enabled
        }

      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          'Failed to save Cash Call settings'

        this.$bvToast.toast(msg, {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style>
.tpa-dropdown-wrapper {
  max-width: 320px;
}

.cashcall-wrapper {
  padding-top: 8px;
}

.assigned-tpa-list {
  max-width: 420px;
}

.approval-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px 24px;
  max-width: 580px;
}

.approval-card label{
  cursor: pointer;
}
</style>